import { useMemo, useState } from 'react';
import PageHeader from '../components/layout/PageHeader';
import { useAuthContext } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import {
  useTeamMembers,
  useTeamInvitations,
  useRoleCatalog,
  useUpdateMemberRole,
  useResendInvitation,
  useRevokeInvitation,
} from '../hooks/useTeam';
import type { AccountRole, RoleDefinition, TeamMember } from '../services/team';
import InviteMemberModal from './team/InviteMemberModal';
import { ConfirmDialog } from '../components/shared/ConfirmDialog';
import { useFlag } from '../hooks/useFeatureFlags';

/**
 * Settings · Team (task 636).
 *
 * One table of "who has access": active members and pending invites merged into
 * a single list, so there is no separate hidden invites screen. Owner and admin
 * see an Invite button, inline role selects on member rows, and Resend / Revoke
 * on invited rows; editor and viewer see the same page entirely read-only. The
 * owner row is never editable — ownership transfer is deliberately out of scope.
 *
 * Every role name and summary comes from the AMS role catalog (`useRoleCatalog`)
 * — the Roles card and the invite modal's role list never hardcode capabilities.
 *
 * The membership, role-catalog, and invitation proxies this page reads
 * (`/api/account/members`, `/api/account/roles`, `/api/account/invitations`)
 * are the innovate-service side of tasks 636 + 634. Until they ship, the reads
 * 404 and the page shows an "isn't available yet" state instead of crashing.
 */

// ─── Date helpers ────────────────────────────────────────────────────────────

const parse = (iso: string): Date | null => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d;
};

/** "Mar 2026" — the month a member joined. */
const monthYear = (iso: string): string => {
  const d = parse(iso);
  return d ? d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' }) : '—';
};

/** "Aug 15, 2026" — the day an invite was sent. */
const longDate = (iso: string): string => {
  const d = parse(iso);
  return d ? d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';
};

/** Whole days from now until an ISO instant (negative once past). */
const daysUntil = (iso: string): number | null => {
  const d = parse(iso);
  if (!d) return null;
  return Math.ceil((d.getTime() - Date.now()) / 86_400_000);
};

const expiryLabel = (iso: string): string => {
  const days = daysUntil(iso);
  if (days === null) return '';
  if (days < 0) return 'expired';
  if (days === 0) return 'expires today';
  return `expires in ${days} day${days === 1 ? '' : 's'}`;
};

const plural = (n: number, word: string): string => `${n} ${word}${n === 1 ? '' : 's'}`;

// ─── Badges ──────────────────────────────────────────────────────────────────

const RoleBadge = ({ role }: { role: string }) => (
  <span className="inline-flex items-center rounded-full bg-indigo-100 px-2 py-0.5 text-xs font-semibold text-indigo-700 dark:bg-indigo-500/20 dark:text-indigo-300">
    {role}
  </span>
);

// ─── Page ────────────────────────────────────────────────────────────────────

export default function TeamPage() {
  const toast = useToast();
  const { user } = useAuthContext();
  // Managing the team (member roles and invitations) is gated by
  // innovate.fe.account.team (task 1217), split off innovate.fe.account.manage
  // with the same role-admin + role-owner cohorts, which carry the rule (task
  // 692); the account name / URL slug stay on General under account.manage.
  // This restores the admin capability the Phase 1 role collapse orphaned: the
  // old `owner || admin` checks named a role the catalog no longer emits, so
  // they had silently become owner-only.
  const canManageAccount = useFlag('innovate.fe.account.team');
  const accountName = user?.accountSlug || 'your account';

  const membersQuery = useTeamMembers();
  const invitesQuery = useTeamInvitations();
  const rolesQuery = useRoleCatalog();

  const updateRole = useUpdateMemberRole();
  const resendInvite = useResendInvitation();
  const revokeInvite = useRevokeInvitation();

  const [inviteOpen, setInviteOpen] = useState(false);
  const [pendingRevoke, setPendingRevoke] = useState<{ id: string; email: string } | null>(null);

  const members = membersQuery.data ?? [];
  const invites = invitesQuery.data ?? [];
  const roleCatalog: RoleDefinition[] = rolesQuery.data ?? [];

  /** Roles that can be assigned via invite / role change — never `owner`. */
  const assignableRoles = useMemo(
    () => roleCatalog.filter((r) => r.role !== 'owner' && r.assignable !== false),
    [roleCatalog],
  );

  const handleRoleChange = (member: TeamMember, nextRole: string) => {
    if (nextRole === member.role) return;
    updateRole.mutate(
      { userId: member.userId, update: { role: nextRole as AccountRole } },
      {
        onSuccess: () => toast.success('Role updated', `${member.displayName} is now ${nextRole}.`),
        onError: () => toast.error('Role not changed', 'That change could not be saved. Please try again.'),
      },
    );
  };

  const handleResend = (id: string, email: string) => {
    resendInvite.mutate(id, {
      onSuccess: () => toast.success('Invite resent', `We emailed ${email} a fresh link.`),
      onError: () => toast.error('Invite not resent', 'Please try again.'),
    });
  };

  /**
   * Revoking asks first (task 864).
   *
   * It used to fire on the click: one mis-aimed press on a small pill button
   * next to Resend killed a link someone had already been sent, with no way to
   * undo it — the invitation is not a resource and does not go to the Archive,
   * so the only remedy is to invite them again. It is the shared
   * `ConfirmDialog` rather than `DeleteResourceControl` because an invitation
   * has no dependents, no ledger row and no trash-icon affordance: the existing
   * Revoke pill stays exactly where it was.
   */
  const handleRevoke = () => {
    if (!pendingRevoke) return;
    const { id, email } = pendingRevoke;
    revokeInvite.mutate(id, {
      onSuccess: () => {
        setPendingRevoke(null);
        toast.success('Invite revoked', `${email} can no longer join with that link.`);
      },
      onError: () => {
        setPendingRevoke(null);
        toast.error('Invite not revoked', 'Please try again.');
      },
    });
  };

  const isSelf = (member: TeamMember) => Boolean(user?.userId) && member.userId === user?.userId;
  const canEditRow = (member: TeamMember) => canManageAccount && member.role !== 'owner';

  const membersUnavailable = membersQuery.isError && !membersQuery.isLoading;
  const countsLine = `${plural(members.length, 'member')} · ${plural(invites.length, 'pending invite')}`;

  return (
    <div>
      <PageHeader
        title="Team"
        description="Everyone with access to this account — active members and pending invites in one place."
      />

      {/* Members + invites card */}
      <section
        className="mt-6 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800"
        data-testid="team-card"
      >
        <div className="flex items-start justify-between gap-4 px-6 py-4">
          <div>
            <h2 className="text-base font-semibold text-gray-900 dark:text-white">Team</h2>
            {!membersUnavailable && !membersQuery.isLoading && (
              <p className="mt-0.5 text-sm text-gray-500 dark:text-gray-400" data-testid="team-counts">
                {countsLine}
              </p>
            )}
          </div>
          {canManageAccount && !membersUnavailable && (
            <button
              type="button"
              onClick={() => setInviteOpen(true)}
              data-testid="team-invite-button"
              className="inline-flex items-center gap-1.5 rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 transition-colors"
            >
              + Invite member
            </button>
          )}
        </div>

        {membersQuery.isLoading && (
          <p className="px-6 pb-6 text-sm text-gray-500 dark:text-gray-400" data-testid="team-loading">
            Loading your team…
          </p>
        )}

        {membersUnavailable && (
          <div className="px-6 pb-6" data-testid="team-unavailable">
            <p className="text-sm text-gray-600 dark:text-gray-300">
              Team management isn&apos;t available yet. Once account membership is enabled you&apos;ll
              see your members and invites here.
            </p>
            <button
              type="button"
              onClick={() => membersQuery.refetch()}
              data-testid="team-retry"
              className="mt-2 inline-flex items-center rounded-md border border-gray-300 dark:border-gray-600 px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              Retry
            </button>
          </div>
        )}

        {!membersQuery.isLoading && !membersUnavailable && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="team-table">
              <thead>
                <tr className="border-t border-gray-200 dark:border-gray-700 text-left text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
                  <th className="px-6 py-2.5 font-medium">Member</th>
                  <th className="px-4 py-2.5 font-medium">Role</th>
                  <th className="px-4 py-2.5 font-medium">Joined</th>
                  <th className="px-4 py-2.5 font-medium">Status</th>
                  <th className="px-6 py-2.5 font-medium text-right" aria-label="Actions" />
                </tr>
              </thead>
              <tbody>
                {members.map((member) => (
                  <tr
                    key={`member-${member.userId}`}
                    data-testid={`member-row-${member.userId}`}
                    className="border-t border-gray-200 dark:border-gray-700"
                  >
                    <td className="px-6 py-3">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-900 dark:text-white">
                          {member.displayName}
                        </span>
                        {isSelf(member) && (
                          <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                            You
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">{member.email}</div>
                    </td>
                    <td className="px-4 py-3">
                      {canEditRow(member) && assignableRoles.length > 0 ? (
                        <select
                          value={member.role}
                          onChange={(e) => handleRoleChange(member, e.target.value)}
                          disabled={updateRole.isPending}
                          data-testid={`member-role-select-${member.userId}`}
                          aria-label={`Role for ${member.displayName}`}
                          className="rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-2 py-1 text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-indigo-500 disabled:opacity-50"
                        >
                          {/* Keep the current role selectable even if the catalog omits it. */}
                          {!assignableRoles.some((r) => r.role === member.role) && (
                            <option value={member.role}>{member.role}</option>
                          )}
                          {assignableRoles.map((r) => (
                            <option key={r.role} value={r.role}>
                              {r.label ?? r.role}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <RoleBadge role={member.role} />
                      )}
                    </td>
                    <td className="px-4 py-3 text-gray-600 dark:text-gray-300">
                      {monthYear(member.joinedAt)}
                    </td>
                    <td className="px-4 py-3">
                      <span className="inline-flex items-center rounded-full bg-green-100 px-2 py-0.5 text-xs font-semibold text-green-700 dark:bg-green-500/20 dark:text-green-300">
                        Active
                      </span>
                    </td>
                    <td className="px-6 py-3 text-right text-gray-400">—</td>
                  </tr>
                ))}

                {invites.map((invite) => (
                  <tr
                    key={`invite-${invite.id}`}
                    data-testid={`invite-row-${invite.id}`}
                    className="border-t border-gray-200 dark:border-gray-700 bg-amber-50/60 dark:bg-amber-500/5"
                  >
                    <td className="px-6 py-3">
                      <div className="font-medium text-gray-900 dark:text-white">{invite.email}</div>
                      <div className="text-xs text-amber-700 dark:text-amber-400">
                        Invited {longDate(invite.invitedAt)}
                        {expiryLabel(invite.expiresAt) && ` · ${expiryLabel(invite.expiresAt)}`}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                        {invite.role}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-400">—</td>
                    <td className="px-4 py-3">
                      <span className="inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-800 dark:bg-amber-500/20 dark:text-amber-300">
                        Invited
                      </span>
                    </td>
                    <td className="px-6 py-3 text-right">
                      {canManageAccount && (
                        <div className="flex justify-end gap-2">
                          <button
                            type="button"
                            onClick={() => handleResend(invite.id, invite.email)}
                            disabled={resendInvite.isPending}
                            data-testid={`invite-resend-${invite.id}`}
                            className="rounded-md border border-gray-300 dark:border-gray-600 px-2.5 py-1 text-xs font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 transition-colors"
                          >
                            Resend
                          </button>
                          <button
                            type="button"
                            onClick={() => setPendingRevoke({ id: invite.id, email: invite.email })}
                            disabled={revokeInvite.isPending}
                            data-testid={`invite-revoke-${invite.id}`}
                            className="rounded-md border border-red-200 dark:border-red-900/60 px-2.5 py-1 text-xs font-medium text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 disabled:opacity-50 transition-colors"
                          >
                            Revoke
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}

                {members.length === 0 && invites.length === 0 && (
                  <tr className="border-t border-gray-200 dark:border-gray-700">
                    <td
                      colSpan={5}
                      className="px-6 py-6 text-center text-sm text-gray-500 dark:text-gray-400"
                      data-testid="team-empty"
                    >
                      No members yet.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Roles card — rendered from the AMS role catalog, never hardcoded */}
      <section
        className="mt-5 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800"
        data-testid="roles-card"
      >
        <div className="px-6 py-4">
          <h2 className="text-base font-semibold text-gray-900 dark:text-white">Roles</h2>
          <p className="mt-0.5 text-sm text-gray-500 dark:text-gray-400">
            What each role can do in this account.
          </p>
        </div>
        {rolesQuery.isLoading && (
          <p className="px-6 pb-6 text-sm text-gray-500 dark:text-gray-400">Loading roles…</p>
        )}
        {!rolesQuery.isLoading && roleCatalog.length === 0 && (
          <p className="px-6 pb-6 text-sm text-gray-500 dark:text-gray-400" data-testid="roles-unavailable">
            Role definitions are unavailable right now.
          </p>
        )}
        {!rolesQuery.isLoading && roleCatalog.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <tbody>
                {roleCatalog.map((r) => (
                  <tr
                    key={r.role}
                    data-testid={`role-def-${r.role}`}
                    className="border-t border-gray-200 dark:border-gray-700"
                  >
                    <td className="px-6 py-3 align-top w-28">
                      <RoleBadge role={r.role} />
                    </td>
                    <td className="px-4 py-3 text-gray-600 dark:text-gray-300">{r.summary}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {canManageAccount && (
        <InviteMemberModal
          isOpen={inviteOpen}
          onClose={() => setInviteOpen(false)}
          accountName={accountName}
          assignableRoles={assignableRoles}
        />
      )}

      <ConfirmDialog
        isOpen={pendingRevoke !== null}
        onClose={() => setPendingRevoke(null)}
        onConfirm={handleRevoke}
        isLoading={revokeInvite.isPending}
        confirmStyle="danger"
        confirmLabel="Revoke invite"
        title={`Revoke the invite for ${pendingRevoke?.email ?? ''}?`}
        message="The link they were sent stops working immediately. There is no undo — to let them in later you will have to invite them again."
      />
    </div>
  );
}
